import { styled } from 'styled-components';
import { classic } from '~/themes';
import iconCrownDisabled from '@assets/images/icon/icon-crown-disabled.png';
import iconCrown from '@assets/images/icon/icon-crown.png';
import iconMedalDisabled from '@assets/images/icon/icon-medal-battle-disabled.png';
import iconMedalEnabled from '@assets/images/icon/icon-medal-battle-enabled.png';
import { StoredTheme } from '~/gql/theme/theme';
import { useAuth } from '~/helpers/store/auth';
import { renderFullPath, renderNameOrYou } from '~/utils/common/utils';

type IProps = {
  playerName: string;
  crownCount: number;
  playerBadges?: string[];
  themes: StoredTheme[];
  playerType: 'challenger' | 'challenged';
  isPlaying?: boolean;
  turn?: number;
};

type TProps = IProps;

export interface ICrownBattle {
  order: number;
  isSelected: boolean;
}

export function BattleTrackItem({
  playerName,
  crownCount,
  themes,
  playerBadges,
  playerType,
  isPlaying,
  turn,
}: TProps) {
  const { payload } = useAuth();

  return (
    <Wrapper $playerType={playerType}>
      <Container $playerType={playerType} $isPlaying={isPlaying}>
        {isPlaying && (
          <SDivState $playerType={playerType}>
            <SSpanState>{`Joue ${!!turn && `Tour ${turn}`}`}</SSpanState>
          </SDivState>
        )}

        <SDivNameCrown>
          <SSpan>{renderNameOrYou(playerName, payload?.username)}</SSpan>
          <SDivCrowns>
            {new Array(3).fill(0).map((_, crInd) => (
              <img
                key={crInd}
                src={crownCount > crInd ? iconCrown : iconCrownDisabled}
                alt="crown-logo"
              />
            ))}
          </SDivCrowns>
        </SDivNameCrown>
        <SDivBadges>
          {playerBadges &&
            themes.map((t, ind) => (
              <SDivIcon key={ind}>
                <SImgMedalIcon
                  src={
                    playerBadges.includes(t.id)
                      ? iconMedalEnabled
                      : iconMedalDisabled
                  }
                  alt=""
                />
                <SImgLogo src={renderFullPath(t.fileName)} alt="" />
              </SDivIcon>
            ))}
        </SDivBadges>
      </Container>
    </Wrapper>
  );
}

const Wrapper = styled.div<{ $playerType: 'challenger' | 'challenged' }>`
  padding-top: 24px;
  position: absolute;
  top: 70px;
  bottom: auto;

  left: ${({ $playerType }) => $playerType === 'challenger' && '0'};
  right: ${({ $playerType }) => $playerType === 'challenged' && '0'};

  @media screen and (max-width: 450px) {
    top: ${({ $playerType }) =>
      $playerType === 'challenged' ? '55px' : 'auto'};

    bottom: ${({ $playerType }) => $playerType === 'challenger' && '10px'};
  }

  @media screen and (max-height: 450px) {
    top: ${({ $playerType }) =>
      $playerType === 'challenged' ? '55px' : 'auto'};

    bottom: ${({ $playerType }) => $playerType === 'challenger' && '10px'};
  }
`;

const Container = styled.div<{
  disabled?: boolean;
  $playerType: 'challenger' | 'challenged';
  $isPlaying?: boolean;
}>`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 75px;
  padding: 10px;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  user-select: none;

  width: auto;

  background: ${({ $isPlaying }) =>
    $isPlaying ? 'rgba(234, 122, 73, 0.30)' : 'rgba(255, 255, 255, 0.3)'};

  box-shadow:
    0px -4px 4px 0px rgba(0, 0, 0, 0.25) inset,
    0px 1px 3px 0px rgba(0, 0, 0, 0.25);
  border-top: ${({ $isPlaying }) =>
    $isPlaying ? '2px solid #EA7A49' : '2px solid #fff'};

  border-bottom: ${({ $isPlaying }) =>
    $isPlaying ? '2px solid #EA7A49' : '2px solid #fff'};
  border-left: ${({ $playerType, $isPlaying }) => {
    if ($playerType === 'challenger') return 'none';
    if ($playerType === 'challenged')
      return $isPlaying ? '2px solid #EA7A49' : '2px solid #fff';

    return '2px solid #fff';
  }};
  border-right: ${({ $playerType, $isPlaying }) => {
    if ($playerType === 'challenger')
      return $isPlaying ? '2px solid #EA7A49' : '2px solid #fff';
    if ($playerType === 'challenged') return 'none';

    return '2px solid #fff';
  }};
  border-radius: ${({ $playerType }) => {
    if ($playerType === 'challenger') return '0px 32px 32px 0px';
    if ($playerType === 'challenged') return '32px 0px 0px 32px';

    return '32px';
  }};

  @media screen and (max-height: 800px) {
    padding: 2px 8px;
    border-radius: ${({ $playerType }) => {
      if ($playerType === 'challenger') return '0px 28px 28px 0px';
      if ($playerType === 'challenged') return '28px 0px 0px 28px';

      return '28px';
    }};
  }

  @media screen and (max-width: 600px) {
    padding: 2px 8px;
    min-height: 65px;
  }

  @media screen and (max-width: 450px) {
    padding: 2px 8px;
    min-height: 65px;
    border-radius: ${({ $playerType }) => {
      if ($playerType === 'challenger') return '0px 28px 28px 0px';
      if ($playerType === 'challenged') return '28px 0px 0px 28px';

      return '28px';
    }};
  }
`;

const SDivState = styled.div<{
  $playerType: 'challenger' | 'challenged';
  $isPlaying?: boolean;
}>`
  height: 24px;
  border-radius: ${({ $playerType }) => {
    if ($playerType === 'challenger') return '0px 8px 0px 0px';
    if ($playerType === 'challenged') return '8px 0px 0px 0px';
    return '0';
  }};

  background: #ea7a49;
  padding: 0 15px;
  position: absolute;
  top: ${({ $playerType }) => {
    if ($playerType === 'challenger') return '0';
    if ($playerType === 'challenged') return '0';
    return '0';
  }};
  left: ${({ $playerType }) => {
    if ($playerType === 'challenger') return '0';
  }};
  right: ${({ $playerType }) => {
    if ($playerType === 'challenged') return '0';
  }};
`;

const SDivNameCrown = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const SDivBadges = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const SSpan = styled.span<{ $color?: string; $textAlign?: string }>`
  color: ${({ $color }) => ($color ? $color : `${classic.token?.colorWhite}`)};
  font-family: 'Riffic Free', sans-serif;
  font-weight: 700;
  font-size: 1.25rem;
  text-align: ${({ $textAlign }) => ($textAlign ? $textAlign : 'left')};
  margin-right: 20px;

  @media screen and (max-height: 800px) {
    font-size: 1.05rem;
    margin-right: 15px;
  }

  @media screen and (max-width: 600px) {
    margin-right: 10px;
  }

  @media screen and (max-height: 450px) {
    font-size: 1.05rem;
    margin-right: 10px;
  }

  @media screen and (max-width: 450px) {
    font-size: 1.05rem;
    margin-right: 10px;
  }
`;

const SSpanState = styled.span<{ $color?: string; $textAlign?: string }>`
  color: ${({ $color }) => ($color ? $color : `${classic.token?.colorWhite}`)};
  font-family: 'Riffic Free', sans-serif;
  font-weight: 700;
  font-size: 1rem;
  text-align: ${({ $textAlign }) => ($textAlign ? $textAlign : 'left')};
  text-transform: uppercase;

  @media screen and (max-height: 800px) {
    font-size: 0.8rem;
  }

  @media screen and (max-width: 450px) {
    font-size: 0.8rem;
  }
`;

const SDivCrowns = styled.div`
  align-items: center;
  border-radius: 40px;
  background: rgba(255, 255, 255, 0.4);
  display: flex;
  justify-content: space-between;
  padding: 0 10px;
  img {
    height: 44px;
    width: 40px;

    @media screen and (max-height: 800px) {
      height: 34px;
      width: 34px;
    }
    @media screen and (max-height: 450px) {
      height: 24px;
      width: 24px;
    }

    @media screen and (max-width: 600px) {
      height: 24px;
      width: 24px;
    }
  }
`;

const SDivIcon = styled.div`
  height: 44px;
  width: 40px;
  position: relative;

  @media screen and (max-height: 800px) {
    height: 34px;
    width: 34px;
  }
  @media screen and (max-height: 450px) {
    height: 24px;
    width: 24px;
  }

  @media screen and (max-width: 600px) {
    height: 24px;
    width: 24px;
  }
`;

const SImgMedalIcon = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  object-fit: contain;
  height: 100%;
  width: 100%;
`;

const SImgLogo = styled.img`
  position: absolute;
  top: 15%;
  object-fit: contain;
  left: 50%;
  height: auto;
  transform: translateX(-50%);
  width: 50%;
`;
